import {
  type ImpersonatorSession,
  type UserPreferences,
  type UserSession,
} from '@solinkcorp/authentication';
import { IS_PRODUCTION } from 'src/environment';
import {
  type AnalyticsEvent,
  getTrackingSuperProperties,
  loadTracker,
  overrideModule,
  remapUserFeatures,
  USER_FEATURE_KEY_PREFIX,
} from './common';

// There's supposed to be no lodash in the core, but lodash's startCase wouldn't
// be trivial to recreate, and by importing it from lodash/startCase we avoid
// importing all 500K of lodash.

import startCase from 'lodash/startCase';

interface Mixpanel {
  identify(email: string): void;
  register(properties: Record<string, unknown>): void;
  track(eventName: string, properties: Record<string, unknown>): void;
  people: {
    set(peopleProperties: Record<string, unknown>): void;
    increment(peopleProperties: Record<string, unknown>): void;
  };
}

let mixpanelInstance: Mixpanel | undefined;

const generateMixpanelMock = () => {
  console.debug('Analytics: Using Mock Mixpanel');
  const result: Mixpanel = {
    identify: (email) => {
      console.debug(`Analytics: mixpanel.identify [email="${email}"]`);
    },
    register: (properties) => {
      console.debug(`Analytics: mixpanel.register [superProperties=${JSON.stringify(properties)}]`);
    },
    track: (name, properties) => {
      console.debug(
        `Analytics: mixpanel.track [eventName="${name}", eventProperties=${JSON.stringify(
          properties,
        )}]`,
      );
    },
    people: {
      set: (properties) => {
        console.debug(
          `Analytics: mixpanel.people.set [peopleProperties=${JSON.stringify(properties)}]`,
        );
      },
      increment: (properties) => {
        console.debug(
          `Analytics: mixpanel.people.increment [peopleProperties=${JSON.stringify(properties)}]`,
        );
      },
    },
  };

  return result;
};

let mixpanelErrorShown = false;
function isMixpanelLoaded(mixpanelInstance: Mixpanel | undefined): mixpanelInstance is Mixpanel {
  const isLoaded = !!mixpanelInstance;
  if (!isLoaded && IS_PRODUCTION && !mixpanelErrorShown) {
    console.error(`Mixpanel is not loaded`);
    mixpanelErrorShown = true;
  }
  return isLoaded;
}

export async function _loadMixpanel(): Promise<Mixpanel | undefined> {
  if (!mixpanelInstance) {
    if (overrideModule()) {
      mixpanelInstance = generateMixpanelMock();
    } else {
      mixpanelInstance = await loadTracker('Mixpanel', () => (window as any).mixpanel as Mixpanel);
    }
  }

  return mixpanelInstance;
}

export async function initMixpanel(
  mixpanel: Mixpanel | undefined,
  user: UserSession,
  impersonator: ImpersonatorSession | undefined,
): Promise<void> {
  if (!mixpanel) {
    return;
  }

  try {
    // Set email as the unique identifier for a user (for tracking)
    mixpanel.identify(user.jwtUser.email);

    const superProperties = await getTrackingSuperProperties(user, impersonator);
    const { properties: mixPanelSuperProperties } = convertToMixpanelData('', superProperties);

    mixpanel.register(mixPanelSuperProperties);

    const sessionToUse = impersonator ?? user;

    // impersonation: !!impersonator,
    // userFeatures: ,
    // localeCode: session.profile.preferences?.locale || DEFAULT_LOCALE,
    // isSupportAccessEnabled:

    // Populate user profile (note this can be removed in future once CH API handles Mixpanel user population)
    trackUser({
      properties: {
        $email: superProperties.email,
        $name: sessionToUse.profile.name,
        'Cloud Id': superProperties.cloudId,
        'Reseller Id': superProperties.resellerId,
        'Reseller Name': superProperties.resellerName,
        'Customer Id': superProperties.customerId,
        'Customer Name': superProperties.customerName,
        'User Type': superProperties.userType,
        'Support Access Enabled': sessionToUse.profile.isSupportAccessEnabled,
        'App Version': superProperties.appVersion,
        'Auth0 Id': user.jwtUser.sub,
        'Impersonator Auth0 Id': impersonator?.jwtUser.sub,
        ...remapUserFeatures(user.profile.features),
      },
    });

    // auth0Id: user.jwtUser.sub,
    // impersonatorAuth0Ud: impersonator?.jwtUser.sub,
  } catch (err) {
    console.error('Mixpanel Init Error', err);
  }
}

function convertToMixpanelData(
  name: string,
  properties: Record<string, unknown> | undefined = {},
): {
  name: string;
  properties: Record<string, unknown>;
} {
  const formattedProperties: Record<string, unknown> = {};

  for (const key of Object.keys(properties)) {
    const newKey =
      key.startsWith('$') || key.startsWith(USER_FEATURE_KEY_PREFIX) ? key : startCase(key);
    formattedProperties[newKey] = properties[key];
  }

  return {
    // Mobile sends some names with a literal hyphen ("Clip - Investigation Linked").
    // startCase would drop that hyphen and split the funnel.
    name: name.includes(' - ') ? name : startCase(name),
    properties: formattedProperties,
  };
}

export function trackMixpanelEvent(event: Readonly<AnalyticsEvent>): void {
  const { trackMixpanel, name, properties } = event;

  if (trackMixpanel && isMixpanelLoaded(mixpanelInstance)) {
    try {
      const { name: eventName, properties: eventProperties } = convertToMixpanelData(
        name,
        properties,
      );

      mixpanelInstance.track(eventName, eventProperties);
    } catch (err) {
      console.error('Mixpanel Event Error', err);
    }
  }
}

// Raw track entry point — does NOT pass through startCase or Intercom.
// Use this when you need the event name and prop keys delivered to Mixpanel
// exactly as written (e.g. snake_case tracking plans).
export async function trackMixpanelEventRaw(
  name: string,
  properties: Record<string, unknown>,
): Promise<void> {
  try {
    const mixpanel = await _loadMixpanel();
    if (!mixpanel) return;
    mixpanel.track(name, properties);
  } catch (err) {
    console.error('Mixpanel Raw Event Error', name, err);
  }
}

export function trackUser(params: {
  properties: Record<string, unknown>;
  incrementProperties?: Record<string, number>;
}): void {
  const { properties, incrementProperties } = params;

  if (isMixpanelLoaded(mixpanelInstance)) {
    try {
      // People properties (non-incrementing)
      if (properties && Object.keys(properties).length > 0) {
        const { properties: peopleProperties } = convertToMixpanelData('', properties);
        mixpanelInstance.people.set(peopleProperties);
      }

      // People properties (incrementing)
      if (incrementProperties && Object.keys(incrementProperties).length > 0) {
        const { properties: peopleProperties } = convertToMixpanelData('', incrementProperties);
        mixpanelInstance.people.increment(peopleProperties);
      }
    } catch (err) {
      console.error('Mixpanel User Error', err);
    }
  }
}

export function trackUserPreferenceChange(preferences: Partial<UserPreferences>): void {
  trackUser({
    properties: preferences,
  });
}
